import React from 'react';
import { ErrorEntry } from 'src/utils/validation';
import { emptyProductForm } from '../utils';

type ProductForm = typeof emptyProductForm;

export type ProductValidationErrors = {
  [key: string]: ErrorEntry;
};

const ProductValidationErrorsContext =
  React.createContext<ProductValidationErrors>({});

const goalFieldNames = new Set<keyof ProductForm['goals']>([
  'intended_applications',
  'study_reasons',
  'target_audience',
  'comissioner_name',
  'external_experts_involved',
  'external_experts_description',
  'results_disclosed',
  'assertions_disclosed',
  'results_confidential',
  'assertions_confidential',
  'disclosure_description',
  'comment',
]);

export const getProductComparableFieldValue = (
  values: ProductForm,
  fieldName: string
) => {
  if (goalFieldNames.has(fieldName as keyof ProductForm['goals'])) {
    return values.goals[fieldName as keyof ProductForm['goals']];
  }

  switch (fieldName) {
    case 'ecoinvent_version':
      return values.ecoinvent_version ?? '';
    case 'production_country':
      return values.production_country?.iso_code ?? '';
    case 'cpc_industry':
      return values.cpc_industry?.code ?? '';
    case 'nace_industries':
      return values.nace_industries.map((item) => item.code).join('|');
    case 'distribution_regions':
      return values.distribution_regions.map((item) => item.iso_code).join('|');
    case 'materials':
      return values.materials
        .map(
          (item) =>
            `${item.is_basic ? 'basic' : 'additional'}:${item.material_name}:${
              item.percentage
            }:${item.recycled_share_pct ?? 0}`
        )
        .join('|');
    default:
      return values[fieldName as keyof ProductForm];
  }
};

export const buildProductValidationErrors = (
  errors: Record<string, string>,
  values: ProductForm
): ProductValidationErrors =>
  Object.fromEntries(
    Object.entries(errors).map(([fieldName, message]) => [
      fieldName,
      {
        message,
        error_value: getProductComparableFieldValue(values, fieldName),
      },
    ])
  );

export function ProductValidationErrorsProvider({
  children,
  errors,
}: Readonly<{
  children: React.ReactNode;
  errors: ProductValidationErrors;
}>) {
  return (
    <ProductValidationErrorsContext.Provider value={errors}>
      {children}
    </ProductValidationErrorsContext.Provider>
  );
}

export const useProductFieldError = (fieldName: string, currentValue: any) => {
  const errors = React.useContext(ProductValidationErrorsContext);
  const entry = errors[fieldName];

  if (!entry || entry.error_value !== currentValue) {
    return undefined;
  }

  return entry.message;
};
